// Every word the "good deal for you" meter shows on its own, in one place so it can be tuned without touching
// components. Nothing depends on these strings: the backend decides the verdict (an enum) and writes the detail; this
// file only names the verdicts. Used by the quote card's value meter and the points popover's redemption scale.

import type { QuoteValueSignal, ValueVerdict } from "@/types/price-quote"

/** The word for each verdict, worst first. Keep them short: they sit beside a small meter. */
export const VERDICT_WORDS: Record<ValueVerdict, string> = {
  poor: "Poor",
  fair: "Fair",
  good: "Good",
  exceptional: "Exceptional",
}

/** The verdicts in order along the meter, worst to best. */
export const VERDICT_ORDER: ValueVerdict[] = [
  "poor",
  "fair",
  "good",
  "exceptional",
]

export const VALUE_COPY = {
  /** when we couldn't check (the hotel's site didn't load, no points price): we say so rather than guess */
  unrated: "Not checked",
  /** why we think the traveler cares, under a tapped call */
  because: (why: string) => `Shown because ${why}`,
  /** the toggle for the calls after the headline */
  more: (count: number) => `+${count} more`,
  less: "Show less",
}

/**
 * What each call judges, shown before its verdict so a word like "Poor" says what it's about. Empty for calls whose
 * picture and verdict words already say it ("Perks lost"), to keep the line short on a narrow card.
 */
export const KIND_LABELS: Record<QuoteValueSignal["kind"], string> = {
  points: "Points",
  coverage: "",
  earn: "",
  perks: "",
  breakfast: "Breakfast",
  flexibility: "Cancellation",
  budget: "Budget",
  price: "Price",
  compare: "Price vs. your other quotes",
}

/** Calls whose verdict reads better in their own words than poor-to-exceptional. */
export const KIND_VERDICT_WORDS: Partial<
  Record<QuoteValueSignal["kind"], Partial<Record<ValueVerdict, string>>>
> = {
  coverage: { poor: "Short", fair: "Transfer", good: "Covered" },
  earn: { fair: "Toward a free night", good: "Earns a free night" },
  perks: { poor: "Perks lost", good: "Perks included" },
}
